import React, { useState, useEffect } from 'react';
import { Pagination } from '../components/Pagination';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { FileCheck, Loader2, CheckCircle, XCircle, Eye } from 'lucide-react';
import { branchRequestService } from '../services';
import { BranchRequest } from '../types';
import { useNavigate } from 'react-router-dom';

export const BranchRequestsPage: React.FC = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState<BranchRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<BranchRequest | null>(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewAction, setReviewAction] = useState<'approve' | 'reject'>('approve');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    loadRequests(currentPage, pageSize);
  }, [currentPage, pageSize]);

  const loadRequests = async (page: number = 0, size: number = 10) => {
    try {
      setLoading(true);
      setError(null);
      const response = await branchRequestService.getPendingRequests(page, size);
      setRequests(response.content);
      setTotalElements(response.totalElements);
      setTotalPages(response.totalPages);
    } catch (err: any) {
      setError(err.message || 'Failed to load branch requests');
    } finally {
      setLoading(false);
    }
  };

  const handleReviewClick = (request: BranchRequest, action: 'approve' | 'reject') => {
    setSelectedRequest(request);
    setReviewAction(action);
    setReviewNotes('');
    setReviewDialogOpen(true);
  };

  const handleReviewSubmit = async () => {
    if (!selectedRequest) return;

    try {
      setError(null);
      await branchRequestService.reviewBranchRequest(selectedRequest.id, {
        status: reviewAction === 'approve' ? 'APPROVED' : 'REJECTED',
        reviewNotes: reviewNotes || undefined,
      });
      setReviewDialogOpen(false);
      setSelectedRequest(null);
      setReviewNotes('');
      await loadRequests(currentPage, pageSize);
    } catch (err: any) {
      setError(err.message || 'Failed to review branch request');
    }
  };

  const getRequestTypeLabel = (type: string) => {
    switch (type) {
      case 'CREATE':
        return 'Create New';
      case 'UPDATE':
        return 'Update Existing';
      default:
        return type;
    }
  };

  if (loading && requests.length === 0) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Branch Requests</h1>
          <p className="text-muted-foreground">Review and approve branch creation requests</p>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
          <p className="font-medium">Error</p>
          <p className="text-sm">{error}</p>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Pending Branch Requests</CardTitle>
          <CardDescription>Requests awaiting approval</CardDescription>
        </CardHeader>
        <CardContent>
          {requests.length === 0 ? (
            <div className="text-center py-12">
              <FileCheck className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-sm text-muted-foreground">
                No pending branch requests. New requests will appear here for review.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Branch Name</TableHead>
                  <TableHead>Organization</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Requested By</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {requests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>
                      <Badge variant="outline">{getRequestTypeLabel(request.requestType)}</Badge>
                    </TableCell>
                    <TableCell className="font-medium">{request.name}</TableCell>
                    <TableCell>{request.organization?.name || '-'}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {request.city?.name || request.address}
                    </TableCell>
                    <TableCell>{request.requestedBy?.fullName || '-'}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(request.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/branch-requests/${request.id}`)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleReviewClick(request, 'approve')}
                        >
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleReviewClick(request, 'reject')}
                        >
                          <XCircle className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalElements={totalElements}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
            itemName="requests"
          />
        </CardContent>
      </Card>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {reviewAction === 'approve' ? 'Approve' : 'Reject'} Branch Request
            </DialogTitle>
            <DialogDescription>
              {reviewAction === 'approve'
                ? `Approve the branch request for "${selectedRequest?.name}"?`
                : `Reject the branch request for "${selectedRequest?.name}"?`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="reviewNotes">Notes (Optional)</Label>
              <Textarea
                id="reviewNotes"
                value={reviewNotes}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setReviewNotes(e.target.value)
                }
                placeholder="Add any notes about this decision..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant={reviewAction === 'approve' ? 'default' : 'destructive'}
              onClick={handleReviewSubmit}
            >
              {reviewAction === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BranchRequestsPage;
